import React, { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';
import AddButtonComponent from './AddButtonComponent';
import CarouselComponent from './AddCarousalComponent';
import { useNavigate } from 'react-router-dom';

const CreateTemplateComponent = () => {
    const navigate = useNavigate();
    const [templateName, setTemplateName] = useState('');
    const [category, setCategory] = useState('');
    const [footerContent, setFooterContent] = useState('');
    const [textContent, setTextContent] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [templateOptions, setTemplateOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState('none');
    const [textInput, setTextInput] = useState('');
    const [fileInput, setFileInput] = useState(null);
    const [fileDescription, setFileDescription] = useState('');
    const [fileError, setFileError] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [placeholderCount, setPlaceholderCount] = useState(1);
    const [placeholderValues, setPlaceholderValues] = useState({});
    const [templateData, setTemplateData] = useState(null);
    const [carouselData, setCarouselData] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const fetchTemplateOptions = async () => {
            try {
                const response = await fetch(`${url}/templatetype/false`, { method: 'GET' });
                if (!response.ok) throw new Error('Failed to fetch template options');
                const data = await response.json();
                setTemplateOptions(data);
            } catch (error) {
                console.error('Error fetching template options:', error);
            }
        };
        fetchTemplateOptions();
    }, []);

    const handleJsonChange = (data) => { setTemplateData(data); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let templateContent = null;
        let templatetype = null;
        if (selectedOption === 'Text') { templatetype = 'Text'; templateContent = textContent; }
        else if (selectedOption === 'Image') { templatetype = 'Image'; templateContent = fileInput ? fileInput.name : fileDescription; }
        else if (selectedOption === 'Video') { templatetype = 'Video'; templateContent = fileInput ? fileInput.name : fileDescription; }
        else if (selectedOption === 'Document') { templatetype = 'Document'; templateContent = fileInput ? fileInput.name : fileDescription; }
        else { templateContent = null; }

        const placeholderSampleValues = extractPlaceholders().map((id) => placeholderValues[id] || '');
        const formData = new FormData();
        formData.append('template_name', templateName);
        formData.append('category', category);
        formData.append('template_type', templatetype);
        formData.append('template_header_text', textInput);
        formData.append('template_media_url', templateContent);
        formData.append('template_footer_text', footerContent);
        formData.append('template_message_body', messageBody);
        formData.append('placeholder_sample_values', JSON.stringify(placeholderSampleValues));
        formData.append('carouselData', JSON.stringify(carouselData));
        formData.append('button_json', JSON.stringify(templateData));
        if (fileInput) formData.append('file', fileInput);

        console.log('FormData being sent:');
        for (let [key, value] of formData.entries()) { console.log(`${key}:`, value); }

        const token = localStorage.getItem('token');
        const response = await fetch(`${url}/templatefile`, {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + token },
            body: formData,
        });
        if (!response.ok) { console.error('Error creating template:', response.statusText); return; }
        toast.success('Template created successfully!', { autoClose: 3000 });
        setTimeout(() => navigate('/template'), 3000);
    };

    const handleAddPlaceholder = () => {
        setMessageBody((prev) => `${prev} {{${placeholderCount}}}`);
        setPlaceholderCount((prev) => prev + 1);
    };

    const handlePlaceholderValueChange = (placeholderId, value) => {
        setPlaceholderValues((prev) => ({ ...prev, [placeholderId]: value }));
    };

    const extractPlaceholders = () => {
        const regex = /\{\{(\d+)\}\}/g;
        const matches = [];
        let match;
        while ((match = regex.exec(messageBody)) !== null) { matches.push(parseInt(match[1])); }
        return [...new Set(matches)];
    };

    const handleAddSmiley = (smiley) => { setMessageBody((prev) => `${prev}${smiley}`); };

    const handleTextFormatting = (format) => {
        const selectedText = window.getSelection().toString();
        if (selectedText) {
            const formattedText = format === 'bold' ? `**${selectedText}**` : format === 'italic' ? `*${selectedText}*` : format === 'underline' ? `__${selectedText}__` : selectedText;
            setMessageBody((prev) => prev.replace(selectedText, formattedText));
        }
    };

    return (
        <WrapperComponent>
            <div style={f.page}>
                {/* Page header */}
                <div style={f.pageHeader}>
                    <div>
                        <h5 style={f.pageTitle}>Create New Template</h5>
                        <p style={f.pageSubtitle}>Build a WhatsApp message template for your campaigns</p>
                    </div>
                    <button type="button" style={f.btnSecondary} onClick={() => navigate('/template')}>
                        <i className="bi bi-arrow-left" style={{ marginRight: 6 }}></i>Back
                    </button>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div style={f.layout}>
                        {/* ── LEFT COLUMN ─────────────────────────── */}
                        <div style={f.leftCol}>

                            {/* Section 1 — Basic Info */}
                            <div style={f.section}>
                                <div style={f.sectionHeader}>
                                    <span style={f.stepBadge}>1</span>
                                    <div>
                                        <p style={f.sectionTitle}>Basic Information</p>
                                        <p style={f.sectionDesc}>Name and category of your template</p>
                                    </div>
                                </div>
                                <div style={f.sectionBody}>
                                    <div style={f.fieldGroup}>
                                        <label style={f.label}>Template Name <span style={f.required}>*</span></label>
                                        <input
                                            type="text"
                                            style={{ ...f.input, ...(errorMessage ? f.inputError : {}) }}
                                            value={templateName}
                                            placeholder="e.g. order_confirmation"
                                            required
                                            onChange={(e) => {
                                                const regex = /^[a-z0-9_]*$/;
                                                if (regex.test(e.target.value)) { setTemplateName(e.target.value); setErrorMessage(''); }
                                                else setErrorMessage('Only lowercase letters, numbers, and underscores are allowed.');
                                            }}
                                        />
                                        {errorMessage && <span style={f.fieldError}>{errorMessage}</span>}
                                        <span style={f.hint}>Lowercase letters, numbers and underscores only</span>
                                    </div>
                                    <div style={f.fieldGroup}>
                                        <label style={f.label}>Category <span style={f.required}>*</span></label>
                                        <select style={f.input} value={category} onChange={(e) => setCategory(e.target.value)} required>
                                            <option value="">Select Category</option>
                                            <option value="Marketing">Marketing</option>
                                            <option value="Utility">Utility</option>
                                            <option value="Authentication">Authentication</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2 — Header */}
                            <div style={f.section}>
                                <div style={f.sectionHeader}>
                                    <span style={f.stepBadge}>2</span>
                                    <div>
                                        <p style={f.sectionTitle}>Header <span style={f.optionalBadge}>Optional</span></p>
                                        <p style={f.sectionDesc}>Add a title, image, video, or document above your message</p>
                                    </div>
                                </div>
                                <div style={f.sectionBody}>
                                    <div style={f.fieldGroup}>
                                        <label style={f.label}>Header Type</label>
                                        <div style={f.radioGroup}>
                                            {templateOptions.map((option) => (
                                                <label key={option._id} style={{ ...f.radioPill, ...(selectedOption === option.description ? f.radioPillActive : {}) }}>
                                                    <input
                                                        type="radio"
                                                        name="templateContent"
                                                        value={option.description}
                                                        defaultChecked={option.description === 'None'}
                                                        onChange={(e) => setSelectedOption(e.target.value)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {option.description}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    {selectedOption === 'Text' && (
                                        <div style={f.fieldGroup}>
                                            <label style={f.label}>Header Text</label>
                                            <input
                                                type="text"
                                                style={f.input}
                                                maxLength="60"
                                                placeholder="Enter header text"
                                                value={textInput}
                                                onChange={(e) => { setTextInput(e.target.value); setTextContent(e.target.value); }}
                                            />
                                            <span style={f.charCount}>{textContent.length}/60</span>
                                        </div>
                                    )}
                                    {['Image', 'Video', 'Document'].includes(selectedOption) && (
                                        <div style={f.mediaRow}>
                                            <div style={{ flex: 1 }}>
                                                <label style={f.label}>Upload File</label>
                                                <input type="file" style={f.fileInput} id="fileInput" onChange={(e) => setFileInput(e.target.files[0])} />
                                            </div>
                                            <div style={f.orSep}>or</div>
                                            <div style={{ flex: 1 }}>
                                                <label style={f.label}>Media URL</label>
                                                <input
                                                    type="text"
                                                    style={{ ...f.input, ...(fileError ? f.inputError : {}) }}
                                                    placeholder="https://example.com/file"
                                                    value={fileDescription}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (selectedOption === 'image' && !/\.(jpg|jpeg|png)$/.test(value)) setFileError('Image URL must end with .jpg, .jpeg, or .png');
                                                        else if (selectedOption === 'video' && !/\.(mp4)$/.test(value)) setFileError('Video URL must end with .mp4');
                                                        else if (selectedOption === 'document' && !/\.(pdf)$/.test(value)) setFileError('Document URL must end with .pdf');
                                                        else { setFileDescription(value); setFileError(''); }
                                                    }}
                                                />
                                                {fileError && <span style={f.fieldError}>{fileError}</span>}
                                                <span style={f.hint}>{selectedOption === 'Image' ? 'JPG, JPEG, PNG' : selectedOption === 'Video' ? 'MP4' : 'PDF'} · Max 5MB</span>
                                            </div>
                                        </div>
                                    )}
                                    {selectedOption === 'Carousel' && (
                                        <CarouselComponent onChange={(carouselData) => {
                                            setCarouselData((prev) => ({ ...prev, carousel_json: { media_type: 'image', media_url: 'https://example.com/image.jpg', carousel_body: carouselData } }));
                                        }} />
                                    )}
                                </div>
                            </div>

                            {/* Section 3 — Message Body */}
                            <div style={f.section}>
                                <div style={f.sectionHeader}>
                                    <span style={f.stepBadge}>3</span>
                                    <div>
                                        <p style={f.sectionTitle}>Message Body <span style={f.required}>*</span></p>
                                        <p style={f.sectionDesc}>The main content of your message</p>
                                    </div>
                                </div>
                                <div style={f.sectionBody}>
                                    <div style={f.toolbar}>
                                        <button type="button" style={f.toolBtn} onClick={handleAddPlaceholder} title="Add Placeholder">
                                            <i className="bi bi-braces" style={{ marginRight: 5 }}></i>Add Placeholder
                                        </button>
                                        <div style={f.toolSep}></div>
                                        <button type="button" style={f.toolBtn} onClick={() => handleAddSmiley('😊')}>😊</button>
                                        <button type="button" style={f.toolBtn} onClick={() => handleAddSmiley('😂')}>😂</button>
                                        <div style={f.toolSep}></div>
                                        <button type="button" style={f.toolBtn} onClick={() => handleTextFormatting('bold')} title="Bold"><i className="bi bi-type-bold"></i></button>
                                        <button type="button" style={f.toolBtn} onClick={() => handleTextFormatting('italic')} title="Italic"><i className="bi bi-type-italic"></i></button>
                                        <button type="button" style={f.toolBtn} onClick={() => handleTextFormatting('underline')} title="Underline"><i className="bi bi-type-underline"></i></button>
                                    </div>
                                    <textarea
                                        id="messageBody"
                                        style={f.textarea}
                                        rows="6"
                                        maxLength="1024"
                                        value={messageBody}
                                        onChange={(e) => { setMessageBody(e.target.value); setMessageContent(e.target.value); }}
                                        placeholder="Enter your message here… Use {{1}}, {{2}} for dynamic values"
                                    />
                                    <span style={{ ...f.charCount, textAlign: 'right' }}>{messageContent.length}/1024</span>
                                    {extractPlaceholders().length > 0 && (
                                        <div style={f.placeholderBox}>
                                            <p style={f.placeholderTitle}>
                                                <i className="bi bi-tag" style={{ marginRight: 6, color: '#1565c0' }}></i>
                                                Placeholder Sample Values
                                                <span style={f.placeholderHint}> — Required for WhatsApp approval</span>
                                            </p>
                                            <div style={f.placeholderGrid}>
                                                {extractPlaceholders().map((placeholderId) => (
                                                    <div key={placeholderId} style={f.placeholderRow}>
                                                        <span style={f.placeholderTag}>{`{{${placeholderId}}}`}</span>
                                                        <div style={{ flex: 1, position: 'relative' }}>
                                                            <input
                                                                type="text"
                                                                style={f.input}
                                                                placeholder="Enter sample value"
                                                                maxLength="50"
                                                                value={placeholderValues[placeholderId] || ''}
                                                                onChange={(e) => handlePlaceholderValueChange(placeholderId, e.target.value)}
                                                            />
                                                            <span style={{ ...f.charCount, position: 'absolute', right: 8, bottom: 8 }}>
                                                                {(placeholderValues[placeholderId] || '').length}/50
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Section 4 — Buttons */}
                            <div style={f.section}>
                                <div style={f.sectionHeader}>
                                    <span style={f.stepBadge}>4</span>
                                    <div>
                                        <p style={f.sectionTitle}>Buttons <span style={f.optionalBadge}>Optional</span></p>
                                        <p style={f.sectionDesc}>Add quick reply or call-to-action buttons</p>
                                    </div>
                                </div>
                                <div style={f.sectionBody}>
                                    <AddButtonComponent templateCategory={category} onChange={handleJsonChange} />
                                </div>
                            </div>

                            {/* Section 5 — Footer */}
                            <div style={f.section}>
                                <div style={f.sectionHeader}>
                                    <span style={f.stepBadge}>5</span>
                                    <div>
                                        <p style={f.sectionTitle}>Footer <span style={f.optionalBadge}>Optional</span></p>
                                        <p style={f.sectionDesc}>Short note shown below the message</p>
                                    </div>
                                </div>
                                <div style={f.sectionBody}>
                                    <div style={f.fieldGroup}>
                                        <input
                                            type="text"
                                            style={f.input}
                                            placeholder="e.g. Reply STOP to unsubscribe"
                                            maxLength="60"
                                            value={footerContent}
                                            onChange={(e) => setFooterContent(e.target.value)}
                                        />
                                        <span style={f.charCount}>{footerContent.length}/60</span>
                                    </div>
                                </div>
                            </div>

                            {/* Submit */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" style={f.btnSecondary} onClick={() => navigate('/template')}>Cancel</button>
                                <button type="submit" style={f.btnPrimary}>
                                    <i className="bi bi-send" style={{ marginRight: 8 }}></i>
                                    Create Template
                                </button>
                            </div>
                        </div>

                        {/* ── RIGHT COLUMN — Live Preview ─────────── */}
                        <div style={f.rightCol}>
                            <div style={f.previewSticky}>
                                <p style={f.previewLabel}>
                                    <i className="bi bi-phone" style={{ marginRight: 6, color: '#1565c0' }}></i>
                                    Live Preview
                                </p>
                                <div style={f.waPhone}>
                                    <div style={f.waBar}>
                                        <div style={f.waAvatar}><i className="bi bi-whatsapp" style={{ fontSize: 14 }}></i></div>
                                        <span style={f.waName}>{templateName || 'Template Preview'}</span>
                                        <i className="bi bi-three-dots-vertical" style={{ color: 'white', marginLeft: 'auto' }}></i>
                                    </div>
                                    <div style={f.waBody}>
                                        {selectedOption === 'Image' && fileDescription && (
                                            <img src={fileDescription} alt="Preview" style={f.waMedia} onError={e => e.target.style.display = 'none'} />
                                        )}
                                        {selectedOption === 'Video' && (
                                            <div style={f.waVideoThumb}>
                                                <i className="bi bi-play-circle-fill" style={{ fontSize: 32, color: 'white', opacity: 0.85 }}></i>
                                            </div>
                                        )}
                                        {selectedOption === 'Document' && (
                                            <div style={f.waDocThumb}>
                                                <i className="bi bi-file-earmark-pdf" style={{ fontSize: 28, color: '#dc2626' }}></i>
                                                <span style={{ fontSize: 12, color: '#374151' }}>Document</span>
                                            </div>
                                        )}
                                        <div style={f.waBubble}>
                                            {selectedOption === 'Text' && textInput && (
                                                <p style={f.waBubbleHeader}>{textInput}</p>
                                            )}
                                            {messageBody ? (
                                                <p style={f.waBubbleBody}>{messageBody}</p>
                                            ) : (
                                                <p style={f.waBubblePlaceholder}>Your message will appear here…</p>
                                            )}
                                            {footerContent && <p style={f.waBubbleFooter}>{footerContent}</p>}
                                            <p style={f.waBubbleTime}>
                                                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                <i className="bi bi-check2-all" style={{ marginLeft: 4, color: '#53bdeb' }}></i>
                                            </p>
                                        </div>
                                        {templateData && templateData.length > 0 && (
                                            <div style={f.waBtns}>
                                                {templateData.filter(btn => btn.type !== 'opt_out').map((btn, i) => (
                                                    <div key={i} style={f.waBtn}>
                                                        {btn.type === 'quick_reply' && <><i className="bi bi-reply" style={f.waBtnIcon}></i>{btn.content}</>}
                                                        {btn.type === 'visit_website' && <><i className="bi bi-globe" style={f.waBtnIcon}></i>{btn.button}</>}
                                                        {btn.type === 'call_phone' && <><i className="bi bi-telephone" style={f.waBtnIcon}></i>{btn.button}</>}
                                                        {btn.type === 'copy_offer' && <><i className="bi bi-clipboard" style={f.waBtnIcon}></i>{btn.site_url}</>}
                                                    </div>
                                                ))}
                                                {templateData.some(btn => btn.type === 'opt_out') && (
                                                    <div style={f.waBtn}>
                                                        <i className="bi bi-x-circle" style={f.waBtnIcon}></i>Stop Promotion
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {category && (
                                    <div style={f.previewMeta}>
                                        <span style={f.previewMetaBadge}>{category}</span>
                                        {templateName && <span style={f.previewMetaName}>{templateName}</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
                <ToastContainer />
            </div>
        </WrapperComponent>
    );
};

const f = {
    page: { display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    layout: { display: 'flex', gap: 20, alignItems: 'flex-start' },
    leftCol: { flex: 2, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 },
    rightCol: { width: 300, flexShrink: 0 },
    previewSticky: { position: 'sticky', top: 16 },
    section: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },
    sectionHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #f3f4f6' },
    stepBadge: { width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
    sectionTitle: { margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827' },
    sectionDesc: { margin: '2px 0 0', fontSize: '12px', color: '#6b7280' },
    sectionBody: { padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    required: { color: '#ef4444', marginLeft: 2 },
    optionalBadge: { fontSize: '11px', background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 4, fontWeight: 500, marginLeft: 6 },
    input: { padding: '9px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', fontFamily: 'inherit' },
    inputError: { borderColor: '#ef4444' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    hint: { fontSize: '11px', color: '#9ca3af' },
    charCount: { fontSize: '11px', color: '#9ca3af', display: 'block' },
    radioGroup: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    radioPill: { display: 'inline-flex', alignItems: 'center', padding: '6px 14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: '500', userSelect: 'none' },
    radioPillActive: { border: '1.5px solid #1565c0', background: '#eff6ff', color: '#1565c0' },
    mediaRow: { display: 'flex', alignItems: 'flex-start', gap: 12 },
    orSep: { fontSize: '13px', color: '#9ca3af', paddingTop: 32, flexShrink: 0 },
    fileInput: { width: '100%', padding: '8px 10px', fontSize: '13px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' },
    toolbar: { display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', background: '#f8fafc', border: '1.5px solid #e5e7eb', borderBottom: 'none', borderRadius: '8px 8px 0 0', flexWrap: 'wrap' },
    toolBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '5px', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center' },
    toolSep: { width: 1, height: 18, background: '#e5e7eb', margin: '0 4px' },
    textarea: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '0 0 8px 8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', resize: 'vertical', fontFamily: 'inherit' },
    placeholderBox: { background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '14px 16px' },
    placeholderTitle: { margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#1565c0', display: 'flex', alignItems: 'center' },
    placeholderHint: { fontSize: '11px', color: '#6b7280', fontWeight: 400 },
    placeholderGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
    placeholderRow: { display: 'flex', alignItems: 'center', gap: 10 },
    placeholderTag: { background: '#1565c0', color: 'white', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
    previewLabel: { fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
    waPhone: { borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '6px solid #1a1a2e' },
    waBar: { background: '#075e54', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 },
    waAvatar: { width: 30, height: 30, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 },
    waName: { color: 'white', fontSize: '13px', fontWeight: '600', flex: 1 },
    waBody: { background: '#ece5dd', padding: '12px', minHeight: 200, display: 'flex', flexDirection: 'column', gap: 8 },
    waMedia: { width: '100%', borderRadius: '8px', objectFit: 'cover', maxHeight: 140 },
    waVideoThumb: { width: '100%', height: 100, background: 'linear-gradient(135deg, #1565c0, #0d2b5e)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    waDocThumb: { background: 'white', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 },
    waBubble: { background: 'white', borderRadius: '0 8px 8px 8px', padding: '10px 12px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    waBubbleHeader: { margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#111827' },
    waBubbleBody: { margin: 0, fontSize: '13px', color: '#111827', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
    waBubblePlaceholder: { margin: 0, fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' },
    waBubbleFooter: { margin: '6px 0 0', fontSize: '11px', color: '#6b7280' },
    waBubbleTime: { margin: '4px 0 0', fontSize: '10px', color: '#9ca3af', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' },
    waBtns: { display: 'flex', flexDirection: 'column', gap: 4 },
    waBtn: { background: 'white', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#1565c0', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    waBtnIcon: { marginRight: 6, fontSize: 12 },
    previewMeta: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 },
    previewMetaBadge: { fontSize: '11px', background: '#eff6ff', color: '#1565c0', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' },
    previewMetaName: { fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '10px 24px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default CreateTemplateComponent;
